import { useContext, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import GreenSwitch from '../../ConfigPayroll/GreenSwitch';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import CircularProgress from '@mui/material/CircularProgress';
import {
  GET_ALL_PAQUETES,
  GET_ALL_PRESTACIONES,
  UPDATE_PAQUETE_STATUS,
  DELETE_PAQUETE,
} from '../../../Querys/prestaciones/querys';
import { WarningAlert } from '../../../alerts/WarningAlert';
import { ErrorAlert } from '../../../alerts/errorAlert';
import { AdminContext } from '../../../context/AdminContext/AdminContext';
import { GET_ALL_PROJECT } from '../../../Querys/querys';
import PackageModal from './PackageModal';
import DeleteConfirmModal from '../DeleteConfirmModal';
import PackageHistoryModal from './PackageHistoryModal';
import '../css/Prestaciones.css';

type Order = 'asc' | 'desc';
type SortKey = 'nombre' | 'proyecto' | 'status';
type StatusFilter = 'todas' | 'activas' | 'inactivas';

interface PackagesPageProps {
  isCreateOpen: boolean;
  onCloseCreate: () => void;
}

const getProyecto = (pkg: any): string => {
  if (!pkg.project) return String(pkg.idProject);
  const { proyectName, client } = pkg.project;
  return client ? `${proyectName} / ${client}` : proyectName;
};

const descendingComparator = (a: any, b: any, orderBy: SortKey): number => {
  const valA = orderBy === 'proyecto' ? getProyecto(a) : orderBy === 'status' ? (a.status ? 1 : 0) : a[orderBy];
  const valB = orderBy === 'proyecto' ? getProyecto(b) : orderBy === 'status' ? (b.status ? 1 : 0) : b[orderBy];
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
};

const getComparator = (order: Order, orderBy: SortKey) =>
  order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);

const PackagesPage = ({ isCreateOpen, onCloseCreate }: PackagesPageProps) => {
  const { adminState } = useContext(AdminContext);
  const idUsuario = adminState?.id ?? '';

  const [editingPackage, setEditingPackage] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [historyTarget, setHistoryTarget] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('activas');
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortKey>('nombre');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: paquetesData, loading: loadingPaquetes, error: errorPaquetes } =
    useQuery(GET_ALL_PAQUETES);
  const { data: prestacionesData, loading: loadingPrestaciones } = useQuery(GET_ALL_PRESTACIONES);
  const { data: projectsData } = useQuery(GET_ALL_PROJECT);

  const paquetes: any[] = paquetesData?.GET_ALL_PAQUETES ?? [];
  const allPrestaciones: any[] = prestacionesData?.GET_ALL_PRESTACIONES ?? [];
  const allProjects: any[] = projectsData?.GET_ALL_PROJECT ?? [];

  const [updateStatus] = useMutation(UPDATE_PAQUETE_STATUS, {
    refetchQueries: [{ query: GET_ALL_PAQUETES }],
  });
  const [deletePaquete] = useMutation(DELETE_PAQUETE, {
    refetchQueries: [{ query: GET_ALL_PAQUETES }],
  });

  const handleToggleStatus = async (pkg: any) => {
    if (pkg.status === true) {
      const count = pkg.countColaboradores ?? 0;
      const texto = count > 0
        ? `Al desactivar este paquete dejará de ser visible en la app móvil de los ${count} colaborador${count === 1 ? '' : 'es'} asignados. ¿Deseas continuar?`
        : '¿Deseas desactivar este paquete?';
      const result = await WarningAlert({ text: texto, showDenyButton: true, confirmButtonText: 'Continuar' });
      if (!result?.isConfirmed) return;
    }
    await updateStatus({ variables: { id: String(pkg.id), idUsuario } });
  };

  const handleDelete = (pkg: any) => {
    setDeleteTarget(pkg);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePaquete({ variables: { id: String(deleteTarget.id), idUsuario } });
    } catch (e: any) {
      const msg = e?.message?.includes('PACKAGE_HAS_COLLABORATORS')
        ? 'No se puede eliminar: el paquete tiene colaboradores asignados.'
        : 'Ocurrió un error al eliminar el paquete.';
      ErrorAlert({ text: msg });
    }
  };

  const handleSort = (col: SortKey) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  const filteredByStatus = useMemo(() => {
    if (statusFilter === 'activas') return paquetes.filter((pkg) => pkg.status === true);
    if (statusFilter === 'inactivas') return paquetes.filter((pkg) => pkg.status === false);
    return paquetes;
  }, [paquetes, statusFilter]);

  const filteredAndSorted = useMemo(() => {
    const term = search.trim().toUpperCase();
    const filtered = term
      ? filteredByStatus.filter(
          (pkg) =>
            pkg.nombre?.toUpperCase().includes(term) ||
            getProyecto(pkg).toUpperCase().includes(term)
        )
      : filteredByStatus;
    return [...filtered].sort(getComparator(order, orderBy));
  }, [filteredByStatus, search, order, orderBy]);

  const paginated = useMemo(
    () => filteredAndSorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredAndSorted, page, rowsPerPage]
  );

  const sortableHeader = (label: string, col: SortKey) => (
    <TableCell style={{ fontWeight: 600 }} align={col === 'status' ? 'center' : 'left'}>
      <TableSortLabel
        active={orderBy === col}
        direction={orderBy === col ? order : 'asc'}
        onClick={() => handleSort(col)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const renderTable = () => {
    if (loadingPaquetes) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress style={{ color: '#FABB00' }} />
        </div>
      );
    }

    if (errorPaquetes) {
      return (
        <div style={{ padding: 24, color: '#555', fontFamily: 'Roboto' }}>
          No se pudieron cargar los paquetes. El servicio de backend aún no está disponible.
        </div>
      );
    }

    return (
      <Box p={2}>
        <div className="containerBtnFindFilter">
          <div style={{
            display: 'flex',
            alignItems: 'center',
            width: 400,
            height: 40,
            padding: '8px 12px 8px 15px',
            border: '0.8px solid #c7ccdc',
            borderRadius: 8,
            boxSizing: 'border-box',
          }}>
            <input
              style={{
                border: 0,
                outline: 'none',
                flex: 1,
                fontSize: 14,
                color: '#838383',
                fontFamily: 'Roboto',
              }}
              className="collaboratorTab__searcher"
              placeholder="Buscar por nombre o proyecto…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
            <img src="/assets/icons/icono-buscar.svg" alt="Buscar" style={{ width: 20, flexShrink: 0, color: '#7f7f81' }} />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['todas', 'activas', 'inactivas'] as StatusFilter[]).map((f) => (
              <div
                key={f}
                className={`btnViewMode ${statusFilter === f ? 'active' : ''}`}
                onClick={() => { setStatusFilter(f); setPage(0); }}
              >
                <span style={{ textTransform: 'capitalize' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        <TableContainer component={Paper} style={{ marginTop: 8 }}>
          <Table aria-label="paquetes table">
            <TableHead style={{ backgroundColor: '#f5f5f5' }}>
              <TableRow>
                {sortableHeader('Nombre', 'nombre')}
                {sortableHeader('Proyecto / Cliente', 'proyecto')}
                <TableCell style={{ fontWeight: 600 }} align="center">Prestaciones</TableCell>
                <TableCell style={{ fontWeight: 600 }} align="center">Colaboradores</TableCell>
                {sortableHeader('Estatus', 'status')}
                <TableCell style={{ fontWeight: 600 }} align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ color: '#999', padding: 32 }}>
                    {search
                      ? 'Sin resultados para la búsqueda.'
                      : statusFilter !== 'todas'
                        ? `No hay paquetes ${statusFilter}.`
                        : 'No hay paquetes registrados. Crea el primero con el botón "Nuevo Paquete".'}
                  </TableCell>
                </TableRow>
              )}
              {paginated.map((pkg: any) => (
                <TableRow key={pkg.id} hover>
                  <TableCell>{pkg.nombre}</TableCell>
                  <TableCell>{getProyecto(pkg)}</TableCell>
                  <TableCell align="center">{pkg.prestaciones?.length ?? 0}</TableCell>
                  <TableCell align="center">{pkg.countColaboradores ?? 0}</TableCell>
                  <TableCell align="center">
                    <GreenSwitch
                      updatePayrollStatus={() => handleToggleStatus(pkg)}
                      id={pkg.id}
                      status={pkg.status ? 'Activo' : 'Inactivo'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => setEditingPackage(pkg)}
                      title="Editar paquete"
                    >
                      <EditIcon fontSize="small" style={{ color: '#555' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setHistoryTarget(pkg)}
                      title="Ver historial"
                    >
                      <HistoryIcon fontSize="small" style={{ color: '#555' }} />
                    </IconButton>
                    {(pkg.countColaboradores ?? 0) === 0 && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(pkg)}
                        title="Eliminar paquete"
                      >
                        <DeleteIcon fontSize="small" style={{ color: '#e53935' }} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAndSorted.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </TableContainer>
      </Box>
    );
  };

  return (
    <>
      {renderTable()}

      <PackageModal
        open={isCreateOpen}
        onClose={onCloseCreate}
        mode="create"
        existingPackages={paquetes}
        allProjects={allProjects}
        allPrestaciones={allPrestaciones}
        loadingPrestaciones={loadingPrestaciones}
      />

      {editingPackage && (
        <PackageModal
          open={editingPackage !== null}
          onClose={() => setEditingPackage(null)}
          mode="edit"
          packageData={editingPackage}
          existingPackages={paquetes}
          allProjects={allProjects}
          allPrestaciones={allPrestaciones}
          loadingPrestaciones={loadingPrestaciones}
        />
      )}

      <DeleteConfirmModal
        open={deleteTarget !== null}
        title={`¿Estás seguro de que quieres eliminar el paquete "${deleteTarget?.nombre}"?`}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />

      <PackageHistoryModal
        open={historyTarget !== null}
        packageData={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </>
  );
};

export default PackagesPage;
